/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAgency } from '@pages/authenticated/agency-pages/AgenciesContext';
import { getPortalLabels } from '../components/PortalSettings/constants';
import { usePortalSettingsOptional } from './portal-settings-context';
import { buildSettingsPending, type SettingsPendingItem } from '../utils/build-settings-pending';
import type { SettingsNavOptions, SettingsSection } from '../constants/settings-nav';

/** The band's chips plus the set of tabs that carry a dot. */
export interface SettingsPending {
  items: SettingsPendingItem[];
  sections: Set<SettingsSection>;
}

/**
 * Resolves what is still unconfigured, from the data the page already holds.
 *
 * @param navOptions - The same gating options that filter the tabs.
 * @returns The chips and the sections to dot.
 */
export function useSettingsPending(navOptions: SettingsNavOptions): SettingsPending {
  const { t } = useTranslation('features/agency-settings');
  const { agency } = useAgency((state) => state);
  const portalState = usePortalSettingsOptional();
  const settings = portalState?.settings ?? null;

  return useMemo(() => {
    const portalLabels = Object.fromEntries(getPortalLabels(t).map((p) => [p.id, p.label]));
    const items = buildSettingsPending({
      agency,
      portals: settings
        ? { connected_portals: settings.connected_portals, enabled_portals: settings.enabled_portals }
        : null,
      portalLabels,
      t,
      navOptions,
    });
    return { items, sections: new Set(items.map((item) => item.section)) };
  }, [agency, settings, t, navOptions]);
}
